import React from 'react';

import { isEmpty, makeTime } from '@nfctron/nexus-core';

import { useQuery } from '@tanstack/react-query';
import { parse } from 'cookie';

import { apiClient } from '@root/features/api-client/api-client';
import { useApiClient } from '@root/features/api-client/api-client.context';
import { AUTH_MARKER_COOKIE_KEY, useSessionStore } from '@root/features/authentication/stores/sessionStore';
import { type UserAccount } from '@root/features/authentication/types/userAccount';

/**
 * Check if auth marker cookie exists in browser
 *
 * We use a non-HTTP-only marker cookie to detect authentication state
 * without making unnecessary API calls
 */
function hasAuthCookie(): boolean {
	try {
		/** skip check on server side */
		if (typeof document === 'undefined') return true;

		/** get cookies from document */
		const cookies = parse(document.cookie || '');

		/** check if auth marker exists in cookies */
		return !isEmpty(cookies[AUTH_MARKER_COOKIE_KEY]);
	} catch (e) {
		return false;
	}
}

export const activeSessionQueryKey = ['activeSession'] as const;
export async function activeSessionQueryFn() {
	/** send GET request to user endpoint */
	const res = await apiClient.get('/api/auth/user', {
		withCredentials: true,
		baseURL: '/',
	});

	/** return the user account object from response data */
	return res.data.userAccount as UserAccount | null;
}

/** custom hook to handle active user session state */
export const useActiveSession = () => {
	/** get session store methods */
	const { setAccount, setIsAuthenticated } = useSessionStore();

	/** get api clients */
	const { passApi, rootApi } = useApiClient();

	/** query for active user session data */
	const query = useQuery({
		queryKey: activeSessionQueryKey,
		queryFn: activeSessionQueryFn,
		staleTime: makeTime('10 minutes'),
		refetchInterval: makeTime('10 minutes'),
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchOnMount: true,
		retry: 0,
		/** skip the query if no auth marker cookie is present */
		enabled: hasAuthCookie(),
	});

	/** useEffect to handle side effects based on query status and data */
	React.useEffect(() => {
		if (query.status === 'success') {
			/** update session store */
			setAccount(query.data);
			setIsAuthenticated(true);
			passApi.setSecurityData(query.data);
			rootApi.setSecurityData(query.data);
		} else if (query.status === 'error') {
			/** clear session and log out the error message */
			setAccount(null);
			setIsAuthenticated(false);
			passApi.setSecurityData(null);
			rootApi.setSecurityData(null);
			console.error('[useActiveSession] Session error:', query.error?.message);
		} else if (!hasAuthCookie()) {
			/** explicitly handle the case when no auth cookie exists */
			setAccount(null);
			setIsAuthenticated(false);
			passApi.setSecurityData(null);
			rootApi.setSecurityData(null);
		}
	}, [query.status, query.data, query.error, setAccount, setIsAuthenticated, passApi, rootApi]);

	return query;
};
